import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, RefreshCw, CheckCircle2, XCircle, Clock } from 'lucide-react';

const TYPE_BADGE = {
  trigger: 'bg-amber-50 text-amber-700',
  fetch_context: 'bg-blue-50 text-blue-700',
  if_else: 'bg-purple-50 text-purple-700',
  send_template: 'bg-emerald-50 text-emerald-700',
  smart_handoff: 'bg-cyan-50 text-cyan-700',
  update_crm_stage: 'bg-pink-50 text-pink-700',
};

export function FlowRunsPage() {
  const { graphId } = useParams();
  const [executions, setExecutions] = useState([]);
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [g, e] = await Promise.all([
        api.get(`/api/flow-graphs/${graphId}`),
        api.get(`/api/flow-graphs/${graphId}/executions`),
      ]);
      setGraph(g);
      setExecutions(e.executions || []);
      if (e.executions?.length && !selectedId) {
        setSelectedId(e.executions[0].id);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [graphId]);

  const selected = executions.find((x) => x.id === selectedId);

  return (
    <div className="space-y-6" data-testid="flow-runs-page">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/dashboard/flow-builder">
            <Button variant="ghost" size="icon"><ArrowLeft className="w-4 h-4" /></Button>
          </Link>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Run history</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {graph?.name ? <>Graph: <span className="font-mono">{graph.name}</span></> : '—'}
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={load} className="gap-2" data-testid="runs-refresh-btn">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-6 min-h-[60vh]">
        {/* Left: list */}
        <div className="col-span-4 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid="runs-list">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-400">Loading…</div>
          ) : executions.length === 0 ? (
            <div className="p-12 text-center">
              <Clock className="w-8 h-8 mx-auto text-slate-300 mb-3" />
              <p className="text-sm font-medium text-slate-700">No runs yet</p>
              <p className="text-xs text-slate-400 mt-1">Activate the graph or do a real-run to see history.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 max-h-[70vh] overflow-y-auto">
              {executions.map((e) => {
                const ok = e.status === 'completed' && e.timeline.every((t) => t.ok);
                return (
                  <button
                    key={e.id}
                    data-testid={`run-row-${e.id}`}
                    onClick={() => setSelectedId(e.id)}
                    className={`w-full text-left p-4 hover:bg-slate-50/50 transition ${selectedId === e.id ? 'bg-slate-50' : ''}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {ok ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-rose-500" />
                        )}
                        <span className="text-xs font-mono text-slate-700">
                          {new Date(e.started_at).toLocaleString()}
                        </span>
                      </div>
                      <span className="text-[10px] uppercase tracking-wide text-slate-400">
                        {e.timeline.length} steps
                      </span>
                    </div>
                    {e.dry_run && (
                      <span className="inline-block mt-1 text-[9px] uppercase font-bold tracking-wider text-amber-600">
                        Dry run
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: timeline detail */}
        <div className="col-span-8 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid="run-detail">
          {!selected ? (
            <div className="p-16 text-center text-sm text-slate-400">
              Select a run on the left to inspect its timeline.
            </div>
          ) : (
            <div className="p-6">
              <div className="flex items-center justify-between gap-4 mb-5">
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-slate-400">Execution</p>
                  <p className="text-xs font-mono text-slate-700">{selected.id}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400">Status</p>
                  <p className={`text-sm font-semibold ${selected.status === 'completed' ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {selected.status}
                  </p>
                </div>
              </div>

              <div className="relative pl-6 border-l-2 border-dashed border-gray-200 space-y-3" data-testid="timeline">
                {selected.timeline.map((t, i) => {
                  const dotColor = t.skipped ? 'bg-amber-400' : (t.ok ? 'bg-emerald-400' : 'bg-rose-400');
                  return (
                    <div key={i} data-testid={`timeline-step-${i}`} className="relative">
                      <span className={`absolute -left-[31px] top-2 w-4 h-4 rounded-full border-4 border-white ${dotColor}`}></span>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${TYPE_BADGE[t.type] || 'bg-slate-100 text-slate-600'}`}>
                            {t.type}
                          </span>
                          {t.skipped && (
                            <span className="text-[9px] uppercase font-bold tracking-wider text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                              skipped
                            </span>
                          )}
                          <span className="text-[10px] text-slate-400">{t.node_id}</span>
                          <span className="text-[10px] text-slate-400 ml-auto">{t.ms}ms</span>
                        </div>
                        <pre className="text-[10px] text-slate-600 whitespace-pre-wrap break-words">
                          {JSON.stringify(t.log, null, 2)}
                        </pre>
                      </div>
                    </div>
                  );
                })}
              </div>

              {selected.context_snapshot && (
                <details className="mt-6 bg-slate-50 rounded-lg p-3" data-testid="context-snapshot">
                  <summary className="text-xs font-semibold text-slate-700 cursor-pointer">
                    Final context snapshot
                  </summary>
                  <pre className="text-[10px] text-slate-600 mt-2 whitespace-pre-wrap break-words">
                    {JSON.stringify(selected.context_snapshot, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
